import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addSkill, deleteSkill } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type DropdownOption = { label: string; value: string };

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
  dropdowns?: {
    categories?: DropdownOption[];
    proficiencyLevels?: DropdownOption[];
  };
};

const SkillsForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack,
  dropdowns
}) => {
  const dispatch = useDispatch();

  const skills = useSelector((state: any) => state.root.resume.skills);

  const [form, setForm] = useState({
    skillName: '',
    category: '',
    proficiencyLevel: '',
    experience: ''
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'experience') {
      if (value && !/^\d{0,4}(\.\d{0,1})?$/.test(value)) return;
      if (value && parseFloat(value) > 99) return;
    }

    if (name === 'skillName' && value.length > 50) return;

    setForm({
      ...form,
      [name]: value
    });
  };

  const handleBlur = (e: any) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const isAddValid =
    form.skillName?.trim() &&
    form.category?.trim() &&
    form.proficiencyLevel?.trim();

  const isContinueValid = skills.length > 0;

  const handleAddSkill = () => {
    if (!isAddValid) return;

    const isDuplicate = skills.some(
      (skill: any) =>
        skill.skillName.toLowerCase() === form.skillName.trim().toLowerCase()
    );

    if (isDuplicate) return;

    dispatch(
      addSkill({
        skillName: form.skillName,
        category: form.category,
        proficiencyLevel: form.proficiencyLevel,
        experience: Number(form.experience)
      })
    );

    setForm({
      skillName: '',
      category: '',
      proficiencyLevel: '',
      experience: ''
    });
    setTouched({});
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>
            <h1 className='text-2xl font-semibold'>{stepName}</h1>
            <p className='mt-1 text-sm text-gray-400'>
              Showcase your specialized skills
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add New Skill</h3>

          {/* skill name */}
          <div className='mb-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Skill Name <span className='text-red-500'>*</span>{' '}
              </label>
              <span
                className={`text-xs ${(form.skillName?.length || 0) >= 50 ? 'text-red-500' : 'text-gray-400'}`}
              >
                {form.skillName?.length || 0}/50
              </span>
            </div>
            <input
              name='skillName'
              value={form.skillName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={50}
              placeholder='Enter skill'
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                touched.skillName && !form.skillName?.trim()
                  ? 'border-red-400 focus:border-red-400'
                  : 'focus:border-gray-300'
              }`}
            />
            {touched.skillName && !form.skillName?.trim() && (
              <p className='mt-1 text-xs text-red-500'>
                Skill name is required
              </p>
            )}
          </div>

          {/* category + proficiency */}
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>
                Category <span className='text-red-500'>*</span>{' '}
              </label>
              <select
                name='category'
                value={form.category}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.category && !form.category?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              >
                <option value=''>Select category</option>
                {dropdowns?.categories?.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {touched.category && !form.category?.trim() && (
                <p className='mt-1 text-xs text-red-500'>
                  Category is required
                </p>
              )}
            </div>

            <div>
              <label className='text-sm font-medium'>
                Proficiency Level <span className='text-red-500'>*</span>{' '}
              </label>
              <select
                name='proficiencyLevel'
                value={form.proficiencyLevel}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.proficiencyLevel && !form.proficiencyLevel?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              >
                <option value=''>Select level</option>
                {dropdowns?.proficiencyLevels?.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {touched.proficiencyLevel && !form.proficiencyLevel?.trim() && (
                <p className='mt-1 text-xs text-red-500'>
                  Proficiency level is required
                </p>
              )}
            </div>
          </div>

          {/* experience */}
          <div className='mt-4'>
            <label className='text-sm font-medium'>Experience (Years)</label>
            <input
              name='experience'
              value={form.experience}
              onChange={handleChange}
              placeholder='Years of experience'
              className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
            />
          </div>

          <Button
            label='+ Save Skill'
            handleClickBtn={handleAddSkill}
            variant='contained'
            disabled={!isAddValid}
            classes='mt-5'
          />
        </div>

        {/* added skills */}
        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Skills</h3>

          {skills.length === 0 ? (
            <p className='text-sm text-gray-400'>
              No skills added yet. Add at least one skill to continue.
            </p>
          ) : (
            <div className='space-y-3'>
              {skills.map((skill: any, index: number) => (
                <div
                  key={skill.skillName}
                  className='flex items-center justify-between rounded-lg border p-3'
                >
                  <div>
                    <p className='font-medium'>{skill.skillName}</p>
                    <p className='text-xs text-gray-500'>
                      {skill.category} &bull; {skill.proficiencyLevel} &bull;{' '}
                      {skill.experience} Years
                    </p>
                  </div>

                  <button
                    onClick={() => dispatch(deleteSkill(skill.skillName))}
                    className='text-xs text-red-500'
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* footer */}
        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            disabled={!isContinueValid}
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SkillsForm;
